import {CSSProperties, useEffect, useId, useRef, useState} from 'react'
import {Check, ChevronDown, X} from 'lucide-react'

interface Opcao {
  id: number
  nome: string
}
interface Props {
  rotulo: string
  opcoes: Opcao[]
  selecionados: number[]
  onChange: (ids: number[]) => void
}

export default function MultiSelect({rotulo, opcoes, selecionados, onChange}: Props) {
  const id = useId()
  const [aberto, setAberto] = useState(false)
  const [rascunho, setRascunho] = useState(selecionados)
  const [filtro, setFiltro] = useState('')
  const [position, setPosition] = useState<CSSProperties>({})
  const ref = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const close = () => {
    setAberto(false)
    trigger.current?.focus()
  }
  const concluir = () => {
    if (
      rascunho.length !== selecionados.length ||
      rascunho.some((optionId) => !selecionados.includes(optionId))
    )
      onChange(rascunho)
    close()
  }

  useEffect(() => {
    if (!aberto) return
    const posicionar = () => {
      const rect = trigger.current?.getBoundingClientRect()
      if (!rect) return
      const width = Math.min(320, window.innerWidth - 32)
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight
      const top = Math.max(12, Math.min(rect.bottom + 8, viewportHeight - 220))
      setPosition({
        position: 'fixed',
        width,
        left: Math.max(16, Math.min(rect.left, window.innerWidth - width - 16)),
        top,
        maxHeight: Math.max(160, viewportHeight - top - 16),
      })
    }
    const outside = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setAberto(false)
    }
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setAberto(false)
        trigger.current?.focus()
      }
    }
    posicionar()
    document.addEventListener('pointerdown', outside)
    document.addEventListener('keydown', key)
    window.addEventListener('resize', posicionar)
    window.addEventListener('scroll', posicionar, true)
    window.visualViewport?.addEventListener('resize', posicionar)
    return () => {
      document.removeEventListener('pointerdown', outside)
      document.removeEventListener('keydown', key)
      window.removeEventListener('resize', posicionar)
      window.removeEventListener('scroll', posicionar, true)
      window.visualViewport?.removeEventListener('resize', posicionar)
    }
  }, [aberto])

  const toggle = (optionId: number) =>
    setRascunho((ids) =>
      ids.includes(optionId) ? ids.filter((s) => s !== optionId) : [...ids, optionId]
    )
  const query = filtro.trim().toLocaleLowerCase('pt-BR')
  const visiveis = opcoes.filter(
    (option) => !query || option.nome.toLocaleLowerCase('pt-BR').includes(query)
  )

  return (
    <div
      className='multi-select'
      ref={ref}
      onBlur={(event) => {
        if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node))
          setAberto(false)
      }}
    >
      <button
        id={`${id}-trigger`}
        ref={trigger}
        type='button'
        aria-expanded={aberto}
        aria-controls={aberto ? `${id}-options` : undefined}
        className={`btn secondary multi-trigger ${selecionados.length ? 'selected' : ''}`}
        onClick={() => {
          if (!aberto) setRascunho(selecionados)
          setAberto((value) => !value)
        }}
      >
        <span>
          {rotulo}
          {selecionados.length > 0 && ` (${selecionados.length})`}
        </span>
        <ChevronDown size={16} aria-hidden='true' />
      </button>
      {aberto && (
        <div
          id={`${id}-options`}
          className='multi-pop'
          style={position}
          role='group'
          aria-labelledby={`${id}-trigger`}
        >
          <div className='multi-pop-header'>
            <strong>{rotulo}</strong>
            <button
              type='button'
              className='icon-button'
              aria-label={`Fechar filtro de ${rotulo.toLowerCase()}`}
              onClick={close}
            >
              <X size={18} />
            </button>
          </div>
          <input
            autoFocus
            type='search'
            aria-label={`Buscar ${rotulo.toLowerCase()}`}
            placeholder={`Buscar ${rotulo.toLowerCase()}…`}
            value={filtro}
            onChange={(event) => setFiltro(event.target.value)}
          />
          <button
            type='button'
            className='btn secondary multi-select-all'
            disabled={opcoes.length === 0 || opcoes.every((option) => rascunho.includes(option.id))}
            onClick={() => setRascunho(opcoes.map((option) => option.id))}
          >
            <Check size={16} aria-hidden='true' /> Selecionar todos
          </button>
          <div className='multi-lista'>
            {visiveis.map((option) => {
              const checked = rascunho.includes(option.id)
              return (
                <button
                  key={option.id}
                  type='button'
                  role='checkbox'
                  aria-checked={checked}
                  className={checked ? 'on' : ''}
                  onClick={() => toggle(option.id)}
                >
                  <span className='check' aria-hidden='true'>
                    {checked && <Check size={14} />}
                  </span>
                  <span>{option.nome}</span>
                </button>
              )
            })}
            {visiveis.length === 0 && <p className='empty !py-4'>Nenhuma opção encontrada.</p>}
          </div>
          {rascunho.length > 0 && (
            <button type='button' className='multi-limpar' onClick={() => setRascunho([])}>
              <X size={14} /> Limpar seleção
            </button>
          )}
          <button type='button' className='btn multi-done' onClick={concluir}>
            Concluir{rascunho.length > 0 && ` (${rascunho.length})`}
          </button>
        </div>
      )}
    </div>
  )
}
